import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { Solution } from '../services/subProjectService';
import ClaimsTab from './ClaimsTab';
import './MetadataEditor.css';

// Fields that have a workspace agent — set of solution field names
const WORKSPACE_ENABLED_FIELDS = new Set(['solution_description', 'fto_analysis', 'patentability', 'patent_application', 'industrialization']);

const SOLUTION_FIELDS: Array<{ key: keyof Solution; labelKey: string; label: string }> = [
  { key: 'solution_description',  labelKey: 'scv2.solutionFields.solution_description',  label: 'Solution Description' },
  { key: 'fto_analysis',          labelKey: 'scv2.solutionFields.fto_analysis',          label: 'Freedom to Operate' },
  { key: 'industrialization',     labelKey: 'scv2.solutionFields.industrialization',     label: 'Industrialization' },
  { key: 'claims',                labelKey: 'scv2.solutionFields.claims',                label: 'Claims' },
  { key: 'patentability',         labelKey: 'scv2.solutionFields.patentability',         label: 'Patentability' },
  { key: 'patent_application',    labelKey: 'scv2.solutionFields.patent_application',    label: 'Patent Application' },
];

interface SolutionsBoxProps {
  solutions: Solution[];
  activeSolutionId: string | null;
  onSolutionChange: (id: string) => void;
  onAddSolution: () => void;
  canEdit: boolean;
  activeField: string | null;
  onFieldFocus: (field: string) => void;
  onFieldChange: (field: string, value: string) => void;
  expandedFields: Record<string, boolean>;
  onToggleExpand: (field: string) => void;
  highlightedFields: Record<string, boolean>;
  subProjectId?: string;
  kbId?: string | null;
}

const SolutionsBox: React.FC<SolutionsBoxProps> = ({
  solutions,
  activeSolutionId,
  onSolutionChange,
  onAddSolution,
  canEdit,
  activeField,
  onFieldFocus,
  onFieldChange,
  expandedFields,
  onToggleExpand,
  highlightedFields,
  subProjectId,
  kbId,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const activeSolution = solutions.find(s => s.id === activeSolutionId) ?? solutions[0] ?? null;

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 4, marginBottom: 6, overflow: 'hidden' }}>
      {/* Header: label + solution tabs + add button */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 10px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#475569', marginRight: 4, whiteSpace: 'nowrap' }}>
          {t('scv2.solutionsLabel', 'Solutions')}
        </span>
        {solutions.map(s => {
          const isActive = s.id === activeSolutionId;
          return (
            <button
              key={s.id}
              onClick={() => onSolutionChange(s.id)}
              style={{
                fontSize: 11,
                padding: '1px 8px',
                borderRadius: 3,
                border: isActive ? '1px solid #2563eb' : '1px solid #cbd5e1',
                background: isActive ? '#2563eb' : '#fff',
                color: isActive ? '#fff' : '#94a3b8',
                fontWeight: isActive ? 600 : 400,
                cursor: 'pointer',
              }}
            >
              {s.name}
            </button>
          );
        })}
        {canEdit && (
          <button
            onClick={onAddSolution}
            title={t('scv2.addSolution', 'Add solution')}
            style={{
              fontSize: 11,
              padding: '1px 8px',
              borderRadius: 3,
              border: '1px dashed #cbd5e1',
              background: 'transparent',
              color: '#94a3b8',
              cursor: 'pointer',
              marginLeft: 'auto',
            }}
          >
            +
          </button>
        )}
      </div>

      {/* Sub-fields */}
      {activeSolution ? (
        SOLUTION_FIELDS.map(({ key, labelKey, label }) => {
          const fieldName = key as string;
          const isExpanded = expandedFields[fieldName] || false;
          const isHighlighted = highlightedFields[fieldName] || false;
          const isActive = activeField === fieldName;
          const value = String((activeSolution as unknown as Record<string, unknown>)[fieldName] ?? '');

          return (
            <div
              key={fieldName}
              className={`metadata-field-row ${isExpanded ? 'expanded' : 'collapsed'} ${isHighlighted ? 'highlighted' : ''} ${isActive ? 'active' : ''}`}
            >
              <div
                className="metadata-field-header"
                onClick={() => onToggleExpand(fieldName)}
                style={{ cursor: 'pointer' }}
              >
                <span className="expand-toggle">{isExpanded ? '▼' : '▶'}</span>
                {fieldName === 'claims' && activeSolution && subProjectId ? (
                  <span
                    className="field-label field-label-link"
                    title={t('workspace.launchButton.title', 'Open workspace agent for this field')}
                    onClick={(e) => {
                      e.stopPropagation();
                      const params = new URLSearchParams();
                      params.set('solutionId', activeSolution.id);
                      params.set('solutionName', activeSolution.name ?? '');
                      if (kbId) params.set('kbId', kbId);
                      navigate(`/workspace/${subProjectId}/claims?${params.toString()}`);
                    }}
                  >
                    {t(labelKey, label)}
                  </span>
                ) : WORKSPACE_ENABLED_FIELDS.has(fieldName) && activeSolution && subProjectId ? (
                  <span
                    className="field-label field-label-link"
                    title={t('workspace.launchButton.title', 'Open workspace agent for this field')}
                    onClick={(e) => {
                      e.stopPropagation();
                      const params = new URLSearchParams();
                      params.set('solutionId', activeSolution.id);
                      params.set('solutionName', activeSolution.name ?? '');
                      if (kbId) params.set('kbId', kbId);
                      navigate(`/workspace/${subProjectId}/${fieldName}?${params.toString()}`);
                    }}
                  >
                    {t(labelKey, label)}
                  </span>
                ) : (
                  <span className="field-label">{t(labelKey, label)}</span>
                )}
                {!isExpanded && value && (
                  <span className="field-preview">
                    {value.substring(0, 80)}{value.length > 80 ? '…' : ''}
                  </span>
                )}

              </div>
              {isExpanded && (
                <div className="metadata-field-body">
                  {fieldName === 'claims' && subProjectId && activeSolution ? (
                    <>
                      <ClaimsTab
                        subProjectId={subProjectId}
                        solutionId={activeSolution.id}
                        canEdit={canEdit}
                      />
                      {/* Raw claims text — collapsible sub-section */}
                      <details style={{ borderTop: '1px solid #e2e8f0', marginTop: 4 }}>
                        <summary style={{ fontSize: 10, color: '#94a3b8', cursor: 'pointer', padding: '3px 8px', userSelect: 'none' }}>
                          {t('scv2.solutionFields.claims_raw', 'Raw text')}
                        </summary>
                        <div style={{ padding: '4px 8px 6px' }}>
                          {canEdit ? (
                            <textarea
                              value={value}
                              onChange={e => onFieldChange(fieldName, e.target.value)}
                              onFocus={() => onFieldFocus(fieldName)}
                              className="metadata-field-textarea"
                              rows={Math.min(Math.max(3, Math.ceil(value.length / 80)), 12)}
                            />
                          ) : (
                            <div className="metadata-field-readonly">{value || '—'}</div>
                          )}
                        </div>
                      </details>
                    </>
                  ) : canEdit ? (
                    <textarea
                      value={value}
                      onChange={e => onFieldChange(fieldName, e.target.value)}
                      onFocus={() => onFieldFocus(fieldName)}
                      className="metadata-field-textarea"
                      rows={Math.min(Math.max(3, Math.ceil(value.length / 80)), 20)}
                    />
                  ) : (
                    <div className="metadata-field-readonly">{value || '—'}</div>
                  )}
                </div>
              )}
            </div>
          );
        })
      ) : (
        <div style={{ padding: '6px 10px', fontSize: 12, color: '#94a3b8' }}>
          {t('scv2.noSolutions', 'No solutions yet.')}
        </div>
      )}

    </div>
  );
};

export default SolutionsBox;
